import { BaseFeature } from '../../core/base-feature.js';

/**
 * CountdownTimerFeature - Frontend feature for controlling the desktop countdown timer
 *
 * Handles:
 * - Starting/stopping/pausing/resuming the timer
 * - Setting label and zero-action
 * - Receiving state updates from desktop
 * - Request/response pattern with pending requests
 */
export class CountdownTimerFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.pendingRequests = new Map();
        this.currentState = null;
    }

    static getMetadata() {
        return {
            id: 'countdown-timer',
            name: 'Countdown Timer',
            version: 'v1.0',
            capabilities: [
                'start', 'pause', 'resume', 'reset', 'stop',
                'set_label', 'set_zero_action', 'get_state'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[CountdownTimer] Initializing...');
        console.log('[CountdownTimer] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                this.currentState = payload;
                this.emit('stateUpdate', payload);
                break;

            case 'start_response':
            case 'pause_response':
            case 'resume_response':
            case 'reset_response':
            case 'stop_response':
            case 'set_label_response':
            case 'set_zero_action_response':
            case 'get_state_response': {
                const originalId = payload?.originalMessageId;
                if (originalId && this.pendingRequests.has(originalId)) {
                    const { resolve } = this.pendingRequests.get(originalId);
                    this.pendingRequests.delete(originalId);
                    resolve(payload);
                }
                break;
            }

            default:
                console.warn(`[CountdownTimer] Unknown command: ${command}`);
        }
    }

    _sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);
            const timeout = setTimeout(() => {
                this.pendingRequests.delete(messageId);
                reject(new Error(`Request '${action}' timed out`));
            }, 10000);

            this.pendingRequests.set(messageId, {
                resolve: (result) => {
                    clearTimeout(timeout);
                    resolve(result);
                },
                reject
            });
        });
    }

    start(seconds, label, zeroAction) {
        return this._sendRequest('start', { seconds, label, zeroAction });
    }

    pause() {
        return this._sendRequest('pause');
    }

    resume() {
        return this._sendRequest('resume');
    }

    reset(seconds) {
        return this._sendRequest('reset', { seconds });
    }

    stop() {
        return this._sendRequest('stop');
    }

    setLabel(label) {
        return this._sendRequest('set_label', { label });
    }

    setZeroAction(zeroAction) {
        return this._sendRequest('set_zero_action', { zeroAction });
    }

    getState() {
        return this._sendRequest('get_state');
    }

    async cleanup() {
        console.log('[CountdownTimer] Cleaning up...');
        for (const [, { reject }] of this.pendingRequests) {
            reject(new Error('Feature cleanup'));
        }
        this.pendingRequests.clear();
    }
}
